import React, { useCallback, useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import debounce from 'lodash/debounce';
import { useSearchBox, useSortBy } from 'react-instantsearch';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './SearchSection.module.css';
import { getAlgoliaIndexNameForLang } from '../../../util/urlHelpers';
import CustomHits from './CustomHits/CustomHits';
import { Button } from '../../../components';
import { propTypes } from '../../../util/types';
import { intlShape, useIntl } from '../../../util/reactIntl';
import VoiceSearch from '../../../components/AlgoliaKeywordSearch/VoiceSearch/VoiceSearch';
import SearchSuggestions, {
  getComboboxInputProps,
  handleSuggestionKeyDown,
} from '../../../components/SearchSuggestions/SearchSuggestions';
import { useSearchSuggestions } from '../../../hooks/useSearchSuggestions';
import IconClose from '../../../components/IconClose/IconClose';
import DesktopResultsFiltersBar from './DesktopResultsFiltersBar';
import MobileResultsFiltersBar from './MobileResultsFiltersBar';
import { useMemo } from 'react';

export const SORT_DEFAULT_OPTION = 'order';
const ALGOLIA_LISTINGS_INDEX = getAlgoliaIndexNameForLang(
  (typeof window !== 'undefined' && localStorage.getItem('appLanguage')) || 'en'
);

// Wait for a real pause in typing before searching. 500ms fired mid-phrase — a
// shopper typing "camping tent" got a search off "camping" before they reached
// the second word (RAS-2591). 1200ms comfortably clears a two-word query; Enter,
// the clear button and voice all still flush immediately (see handlers below).
const SEARCH_DEBOUNCE_MS = 1200;

function CustomSearchBox({
  searchInputText,
  initialQuery,
  onSearchQueryChange,
  hasDateRange,
  onSearchFocus,
  onSearchTouchStart,
  inputRef: inputRefProp,
  suggestionFilters,
}) {
  const intl = useIntl();
  const inputRef = useRef(null);
  // Mirrors components/AlgoliaKeywordSearch/KeywordInput.js's scroll-to-collapse-keyboard
  // behavior, so the search bar behaves the same on this page as on the main search page.
  const isUserInteractingRef = useRef(false);
  const lastFocusTimeRef = useRef(0);
  const setInputRef = useCallback(
    el => {
      inputRef.current = el;
      if (inputRefProp) {
        if (typeof inputRefProp === 'function') inputRefProp(el);
        else inputRefProp.current = el;
      }
    },
    [inputRefProp]
  );

  const { refine, query } = useSearchBox({
    defaultRefinement: initialQuery || '',
  });

  // Local state for immediate input feedback; refine is debounced
  const [localValue, setLocalValue] = useState(initialQuery || '');
  const localValueRef = useRef(localValue);
  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  const debouncedSearch = useMemo(
    () => debounce(value => onSearchQueryChange(value), SEARCH_DEBOUNCE_MS),
    [onSearchQueryChange]
  );

  // Sync local value and refine from initialQuery when it changes (URL navigation, mount,
  // or the parent clearing the query — e.g. picking a category drops the keyword). Cancel
  // any in-flight typing debounce so a stale pending keystroke can't re-apply the old
  // query a beat after the parent reset it.
  useEffect(() => {
    const next = initialQuery ?? '';
    debouncedSearch.cancel();
    setLocalValue(next);
    refine(next);
  }, [initialQuery]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const shouldHighlight =
    (initialQuery && initialQuery.trim().length > 0) || hasDateRange;

  // Focus without scrolling - prevents auto-scroll to search area after search
  useEffect(() => {
    if (shouldHighlight && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [shouldHighlight]);

  const handleChange = event => {
    const value = event.target.value;
    setLocalValue(value);
    debouncedSearch(value);
  };

  // Interim voice results arrive rapidly (roughly one per spoken word). Only
  // update the visible text here — routing each one through the typing
  // debounce would race with the flush in handleVoiceListeningChange below
  // (a mid-utterance pause longer than the debounce but shorter than voice's
  // own silence timeout would fire a premature search with a partial
  // transcript, then get raced by the final one — the "flickers and never
  // settles" symptom). The single authoritative search fires once listening
  // actually stops.
  const handleVoiceSearch = transcript => {
    setLocalValue(transcript);
  };

  const handleVoiceListeningChange = useCallback(isListening => {
    if (!isListening) {
      debouncedSearch.cancel();
      const value = localValueRef.current;
      refine(value);
      onSearchQueryChange(value);
    }
  }, [debouncedSearch, refine, onSearchQueryChange]);

  const handleFocus = () => {
    isUserInteractingRef.current = true;
    lastFocusTimeRef.current = Date.now();
    onSearchFocus?.();
  };

  const handleBlur = () => {
    isUserInteractingRef.current = false;
  };

  const handleTouchStart = () => {
    onSearchTouchStart?.();
  };

  // Collapse the keyboard on scroll, matching components/AlgoliaKeywordSearch/KeywordInput.js.
  // The 500ms grace period after focus exists so iOS's own auto-scroll-into-view (which fires
  // right after tapping the field) doesn't immediately blur what the user just focused.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const handleScroll = () => {
      const isWithinFocusGracePeriod = Date.now() - lastFocusTimeRef.current < 500;
      // Clear the interacting flag before checking it (not after), so a single qualifying
      // scroll event is enough to blur. A program­matic single-shot scroll - e.g. this page's
      // own scroll-results-into-view-after-search feature (scrollToElementWhenStable) - only
      // ever fires one native 'scroll' event, unlike a real touch-scroll gesture which fires
      // many in quick succession. Checking the flag before clearing it required a second
      // event to actually blur, which a real finger-swipe always supplied but a single
      // programmatic scroll never did - confirmed on a real device (RAS).
      if (!isWithinFocusGracePeriod) {
        isUserInteractingRef.current = false;
      }
      if (
        inputRef.current === document.activeElement &&
        !isUserInteractingRef.current &&
        !isWithinFocusGracePeriod
      ) {
        inputRef.current.blur();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Type-ahead suggestions (RAS-2536), scoped to this store. No geo: a single store is a
  // single location, so a radius would filter on nothing meaningful.
  const suggestionsListboxId = 'store-search-suggestions';
  const suggestions = useSearchSuggestions({
    query: localValue,
    aroundLatLng: null,
    useLocation: false,
    filters: suggestionFilters,
    enabled: !!suggestionFilters,
    // The "See all results" row is an option too, so the arrow keys can reach it.
    extraOptions: 1,
  });

  // Choosing a suggestion is an explicit commit, so it flushes the debounce the same way
  // Enter does rather than waiting out SEARCH_DEBOUNCE_MS.
  const commitSearch = useCallback(
    value => {
      debouncedSearch.cancel();
      setLocalValue(value);
      refine(value);
      onSearchQueryChange(value);
    },
    [debouncedSearch, refine, onSearchQueryChange]
  );

  const handleSuggestionSelect = useCallback(
    value => {
      // Dismiss against the SELECTED value, not what is currently in the box — the input has
      // not been updated yet, so a default dismissal would cover the half-typed text and the
      // list would reopen showing the term just chosen.
      suggestions.close(value);
      commitSearch(value);
      inputRef.current?.blur();
    },
    [suggestions, commitSearch]
  );

  // Plain <input>, not a <form>, so Enter has no native submit/blur behavior - the
  // keyboard just sits there. Flushing the debounced search immediately (rather than
  // waiting out SEARCH_DEBOUNCE_MS) matches the "commit now" intent of pressing Enter.
  const handleKeyDown = event => {
    // While the dropdown is open it owns the arrow keys, Enter-on-a-suggestion and Escape.
    if (handleSuggestionKeyDown(event, {
      isOpen: suggestions.isOpen,
      highlighted: suggestions.highlighted,
      moveHighlight: suggestions.moveHighlight,
      onSelect: handleSuggestionSelect,
      onClose: suggestions.close,
    })) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      suggestions.close();
      commitSearch(localValue);
      inputRef.current?.blur();
    }
  };

  const showClear = localValue.trim().length > 0;

  const handleClear = () => {
    debouncedSearch.cancel();
    setLocalValue('');
    refine('');
    onSearchQueryChange('');
    inputRef.current?.focus({ preventScroll: true });
  };

  return (
    <div className={css.searchBoxWrapper}>
      <input
        ref={setInputRef}
        className={classNames(
          css.searchInput,
          showClear && css.searchInputWithClear
        )}
        onChange={handleChange}
        onFocus={handleFocus}
        onClick={handleFocus}
        onBlur={handleBlur}
        onTouchStart={handleTouchStart}
        onKeyDown={handleKeyDown}
        value={localValue}
        type="text"
        autoComplete="off"
        // Lowercase, not the camelCase `enterKeyHint` - this codebase is on React 16, which
        // doesn't recognize the camelCase prop and silently drops it (with a dev warning).
        // The plain lowercase attribute name always passes through to the DOM.
        enterkeyhint="search"
        // Lets StoreProfilePage find and focus this store's own search bar ("Stay on
        // [store]" in the search-scope prompt, RAS-2619). Two instances render (desktop
        // and mobile bars); the page picks whichever one the current breakpoint shows.
        data-store-search-input=""
        placeholder={searchInputText || 'Search by keyword'}
        {...suggestions.compositionHandlers}
        {...getComboboxInputProps({
          isOpen: suggestions.isOpen,
          listboxId: suggestionsListboxId,
          highlightedIndex: suggestions.highlightedIndex,
        })}
      />
      {showClear ? (
        <button
          type="button"
          className={css.searchClearButton}
          onClick={handleClear}
          aria-label={intl.formatMessage({
            id: 'StoreProfilePage.SearchSection.clearSearch',
            defaultMessage: 'Clear search',
          })}
        >
          <IconClose className={css.searchClearIcon} />
        </button>
      ) : null}
      <VoiceSearch
        className={css.voiceSearch}
        onVoiceSearch={handleVoiceSearch}
        onListeningChange={handleVoiceListeningChange}
      />
      <SearchSuggestions
        suggestions={suggestions.suggestions}
        query={localValue}
        isOpen={suggestions.isOpen}
        highlightedIndex={suggestions.highlightedIndex}
        onSelect={handleSuggestionSelect}
        onHighlight={suggestions.setHighlightedIndex}
        listboxId={suggestionsListboxId}
      />
    </div>
  );
}

const SortBy = props => {
  const { sortValue, setSortValue, items } = props;
  // useSortBy requires the current index name to be in the items array
  const itemsWithIndex = [
    { label: ALGOLIA_LISTINGS_INDEX, value: ALGOLIA_LISTINGS_INDEX },
    ...(items || []),
  ];
  const { options, refine } = useSortBy({ ...props, items: itemsWithIndex });
  const defaultOption = { label: 'Sort By: Order', value: SORT_DEFAULT_OPTION };
  return (
    <select
      onChange={event => {
        if (event.target.value !== SORT_DEFAULT_OPTION) {
          refine(event.target.value);
        }
        setSortValue(event.target.value);
      }}
      value={sortValue}
    >
      <option key={defaultOption.value} value={defaultOption.value}>
        {defaultOption.label}
      </option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// const SearchSectionMobile = ({ searchInputText, sortValue, setSortValue, initialQuery, onSearchQueryChange }) => {
//   return (
//     <div className={css.queryContainerMobile}>
//       <div className={css.searchControls}>
//         <CustomSearchBox
//           searchInputText={searchInputText}
//           initialQuery={initialQuery}
//           onSearchQueryChange={onSearchQueryChange}
//         />
//         {/* <SortBy
//           items={[
//             {
//               label: 'Sort By: New Rentals',
//               value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_desc`,
//             },
//             {
//               label: 'Sort By: Old Rentals',
//               value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_asc`,
//             },
//           ]}
//           sortValue={sortValue}
//           setSortValue={setSortValue}
//         /> */}
//       </div>
//     </div>
//   );
// };

const SearchSectionDesktop = ({
  searchInputText,
  initialQuery,
  onSearchQueryChange,
  hasDateRange,
  onSearchFocus,
  onSearchTouchStart,
  searchInputRef,
  suggestionFilters,
}) => {
  return (
    <div className={css.queryContainerDesktop}>
      <div className={css.searchControls}>
        <div className={css.keywordSearch}>
          <CustomSearchBox
            searchInputText={searchInputText}
            initialQuery={initialQuery}
            onSearchQueryChange={onSearchQueryChange}
            hasDateRange={hasDateRange}
            onSearchFocus={onSearchFocus}
            onSearchTouchStart={onSearchTouchStart}
            inputRef={searchInputRef}
            suggestionFilters={suggestionFilters}
          />
        </div>
      </div>
    </div>
  );
};

function SearchQuerySection({
  searchInputText,
  sortValue,
  setSortValue,
  initialQuery,
  onSearchQueryChange,
  hasDateRange,
  suggestionFilters,
}) {
  return (
    <>
      {/* <SearchSectionMobile 
        searchInputText={searchInputText}
        sortValue={sortValue}
        setSortValue={setSortValue}
        initialQuery={initialQuery}
        onSearchQueryChange={onSearchQueryChange}
      /> */}
      <SearchSectionDesktop
        searchInputText={searchInputText}
        initialQuery={initialQuery}
        onSearchQueryChange={onSearchQueryChange}
        hasDateRange={hasDateRange}
        suggestionFilters={suggestionFilters}
      />
    </>
  );
}

const SearchSection = React.forwardRef((props, ref) => {
  const {
    suggestionFilters,
    params,
    routes,
    history,
    user,
    intl,
    onUpdateRecordsOrder,
    updateOrderError,
    config,
    isCurrentUser,
    currentUser,
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency,
    onFetchTransactionLineItems,
    onFetchTimeSlots,
    onAddToCart,
    onUpdateCart,
    filters,
    onCategoryChange,
    storeTimeZone,
    filteredCartItems,
    onSearchQueryChange,
    onListingFacetFiltersChange,
    dateRangePickerRef,
    datePickerAnchorRef,
    storePath,
    totalListings,
    onManageDisableScrolling,
    pendingSearchSettledScrollRef,
    onScrollToSearchSection,
    onScrollToDateField,
  } = props;

  const {
    category: selectedCategoryFilter,
    dateRange: dateRangeFilter,
  } = filters;
  const { profile } = user?.attributes;
  const { displayName, publicData = {} } = profile;
  const { storeName } = publicData;

  const storeUserName = !!storeName ? storeName : displayName;

  const [sortValue, setSortValue] = useState(SORT_DEFAULT_OPTION);
  const [scrollActiveCategory, setScrollActiveCategory] = useState(null);
  const isMobile =
    window && typeof window !== 'undefined' && window.innerWidth < 768;

  // Initialize view mode from sessionStorage or default
  const getInitialViewMode = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return  'list'
    }
    const stored = window.sessionStorage.getItem('storeProfileViewMode');
    if (stored === 'grid' || stored === 'list') {
      return stored;
    }
    return 'list';
  };

  const [viewMode, setViewMode] = useState(getInitialViewMode); // 'grid' | 'list'

  const handleViewModeChange = useCallback(mode => {
    // Persist to sessionStorage
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem('storeProfileViewMode', mode);
    }

    if (typeof document !== 'undefined' && document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => setViewMode(mode));
      });
    } else {
      setViewMode(mode);
    }
  }, []);

  // Use passed searchInputText prop instead of generating it here
  const finalSearchInputText =
    props.searchInputText ||
    intl.formatMessage(
      { id: 'StoreProfilePage.SearchSection.searchInputText' },
      { displayName: storeUserName }
    );

  // Note: Category filter is now handled by Algolia's hierarchical menu refinement
  // Only date range filter needs to be added here if needed
  // Base filter (user.id) is already applied in MainContent's Configure

  return (
    <section ref={ref} className={css.searchSection}>
      {/* InstantSearch wrapper removed - now provided by MainContent */}
      {/* Category filtering is handled by Algolia's hierarchical menu refinement */}

      <div className={css.searchWrapper}>
        <div className={css.buttonsGroup}>
          <Button
            arrow="left"
            icon={<i className="ri-apps-2-line" />}
            variant="secondary-outline"
            className={viewMode === 'grid' ? css.viewButtonActive : ''}
            onClick={() => handleViewModeChange('grid')}
          />
          <Button
            arrow="left"
            icon={<i className="ri-list-check-2" />}
            variant="secondary-outline"
            className={viewMode === 'list' ? css.viewButtonActive : ''}
            onClick={() => handleViewModeChange('list')}
          />
        </div>
      </div>
      <DesktopResultsFiltersBar
        scrollActiveCategory={scrollActiveCategory}
        desktopSearchBar={
          <SearchQuerySection
            searchInputText={finalSearchInputText}
            sortValue={sortValue}
            setSortValue={setSortValue}
            initialQuery={filters.searchQuery}
            hasDateRange={!!filters.dateRange}
            onSearchQueryChange={onSearchQueryChange}
            suggestionFilters={suggestionFilters}
          />
        }
        user={user}
        selectedCategoryFilter={selectedCategoryFilter}
        onCategoryChange={onCategoryChange}
        dateRangeFilter={dateRangeFilter}
        onDateRangeChange={props.onDateRangeChange}
        storeTimeZone={storeTimeZone}
        dateRangePickerRef={dateRangePickerRef}
        datePickerAnchorRef={datePickerAnchorRef}
        onScrollToDateField={onScrollToDateField}
        onManageDisableScrolling={onManageDisableScrolling}
        listingFacetFilters={filters.listingFacetFilters || {}}
        onListingFacetFiltersChange={onListingFacetFiltersChange}
      />
      <MobileResultsFiltersBar
        scrollActiveCategory={scrollActiveCategory}
        mobileSearchBar={
          <CustomSearchBox
            searchInputText={finalSearchInputText}
            initialQuery={filters.searchQuery}
            hasDateRange={!!filters.dateRange}
            onSearchQueryChange={onSearchQueryChange}
            suggestionFilters={suggestionFilters}
          />
        }
        user={user}
        selectedCategoryFilter={selectedCategoryFilter}
        onCategoryChange={onCategoryChange}
        dateRangeFilter={dateRangeFilter}
        onDateRangeChange={props.onDateRangeChange}
        storeTimeZone={storeTimeZone}
        dateRangePickerRef={dateRangePickerRef}
        datePickerAnchorRef={datePickerAnchorRef}
        onScrollToDateField={onScrollToDateField}
        onManageDisableScrolling={onManageDisableScrolling}
        listingFacetFilters={filters.listingFacetFilters || {}}
        onListingFacetFiltersChange={onListingFacetFiltersChange}
      />
      <CustomHits
        routes={routes}
        history={history}
        user={user}
        isCurrentUser={isCurrentUser}
        onUpdateRecordsOrder={onUpdateRecordsOrder}
        updateOrderError={updateOrderError}
        intl={intl}
        sortOption={sortValue}
        storeUserName={storeUserName}
        config={config}
        currentUser={currentUser}
        params={params}
        exchangeRates={exchangeRates}
        fetRatesError={fetRatesError}
        fetchRatesInProgress={fetchRatesInProgress}
        currentUserCurrency={currentUserCurrency}
        onFetchTransactionLineItems={onFetchTransactionLineItems}
        onFetchTimeSlots={onFetchTimeSlots}
        onAddToCart={onAddToCart}
        onUpdateCart={onUpdateCart}
        dateRangeFilter={dateRangeFilter}
        storeTimeZone={storeTimeZone}
        filteredCartItems={filteredCartItems}
        viewMode={viewMode}
        dateRangePickerRef={dateRangePickerRef}
        datePickerAnchorRef={datePickerAnchorRef}
        storePath={storePath}
        totalListings={totalListings}
        selectedCategoryFilter={selectedCategoryFilter}
        onCategoryChange={onCategoryChange}
        searchQuery={filters.searchQuery}
        pendingSearchSettledScrollRef={pendingSearchSettledScrollRef}
        onScrollToSearchSection={onScrollToSearchSection}
        onScrollCategoryChange={setScrollActiveCategory}
      />
    </section>
  );
});

SearchSection.displayName = 'SearchSection';

export { SearchSectionDesktop };
SearchSection.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  routes: PropTypes.arrayOf(propTypes.route).isRequired,
  history: PropTypes.object.isRequired,
  user: propTypes.user.isRequired,
  intl: intlShape.isRequired,
  onUpdateRecordsOrder: PropTypes.func.isRequired,
  updateOrderError: PropTypes.object,
  config: PropTypes.object.isRequired,
  isCurrentUser: PropTypes.bool.isRequired,
  currentUser: propTypes.currentUser,
  exchangeRates: PropTypes.array,
  fetRatesError: PropTypes.object,
  fetchRatesInProgress: PropTypes.bool,
  currentUserCurrency: PropTypes.string.isRequired,
  onFetchTransactionLineItems: PropTypes.func.isRequired,
  onFetchTimeSlots: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onUpdateCart: PropTypes.func,
  filters: PropTypes.shape({
    category: PropTypes.shape({
      categoryId: PropTypes.string,
      depth: PropTypes.number,
    }),
    dateRange: PropTypes.shape({
      startDate: PropTypes.instanceOf(Date),
      endDate: PropTypes.instanceOf(Date),
    }),
    searchQuery: PropTypes.string,
    listingFacetFilters: PropTypes.object,
  }).isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onSearchQueryChange: PropTypes.func,
  onListingFacetFiltersChange: PropTypes.func,
  onManageDisableScrolling: PropTypes.func,
  pendingSearchSettledScrollRef: PropTypes.shape({ current: PropTypes.any }),
  onScrollToSearchSection: PropTypes.func,
  onScrollToDateField: PropTypes.func,
  storeTimeZone: PropTypes.string,
  filteredCartItems: PropTypes.array,
  datePickerAnchorRef: PropTypes.shape({ current: PropTypes.any }),
  storePath: PropTypes.string,
  totalListings: PropTypes.number,
};

export default SearchSection;
